import * as React from "react";
import { Box, Chip, ChipDelete, Select, Option, Stack, Input } from "@mui/joy";
import { Field, DynamicFieldSelection, SupportConfig } from "./types";
import { Clear } from "@mui/icons-material";

interface MultiSelectDynamicOptionsProps {
  field: Field;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  selectedServiceTeam: string;
  dynamicFieldSelection: DynamicFieldSelection;
  allFieldsData: Field[];
}

export default function MultiSelectDynamicOptions({
  field,
  value,
  onChange,
  disabled,
  selectedServiceTeam,
  dynamicFieldSelection,
  allFieldsData,
}: MultiSelectDynamicOptionsProps) {
  const selectedValues = value ? value.split(";").filter((val) => val.trim()) : [];

  const handleSelectChange = (_event: React.SyntheticEvent | null, newValue: {} | string | null) => {
    if (!newValue || selectedValues.includes(newValue as string)) return;

    const newSelectedValues = [...selectedValues, newValue as string];
    onChange(newSelectedValues.join(";"));
  };

  const removeValue = (valueToRemove: string) => {
    const newSelectedValues = selectedValues.filter((val) => val !== valueToRemove);
    onChange(newSelectedValues.join(";"));
  };

  // Check if we have the required mapping
  if (!field.dynamicOptions || !(field.dynamicOptions.mappingFrom in dynamicFieldSelection)) {
    return (
      <Input
        placeholder={`Please select an option from '${allFieldsData.find((f) => f.name === field?.dynamicOptions?.mappingFrom)?.displayName}' first.`}
        disabled={true}
      />
    );
  }

  // Handle both single and multi-select values for mapping lookup
  const mappingSourceValue = dynamicFieldSelection[field.dynamicOptions.mappingFrom];
  const firstSelectedValue = mappingSourceValue.includes(";") ? mappingSourceValue.split(";")[0].trim() : mappingSourceValue;
  const availableOptions = field.dynamicOptions.mapping[firstSelectedValue]?.filter((option) => !selectedValues.includes(option)) || [];

  return (
    <Stack spacing={1}>
      {selectedValues.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {selectedValues.map((selectedValue, index) => (
            <Chip
              key={index}
              variant="soft"
              color="primary"
              endDecorator={
                <ChipDelete
                  color="danger"
                  variant="plain"
                  onClick={() => removeValue(selectedValue)}
                  sx={{
                    minWidth: 24,
                    minHeight: 24,
                    "& svg": { fontSize: "1.2rem" },
                    "@media (pointer: coarse)": {
                      minWidth: 32,
                      minHeight: 32,
                      "& svg": { fontSize: "1.4rem" },
                    },
                  }}
                >
                  <Clear />
                </ChipDelete>
              }
              disabled={disabled}
            >
              {selectedValue}
            </Chip>
          ))}
        </Box>
      )}

      {availableOptions.length > 0 && (
        <Select placeholder="Select option..." value={null} onChange={handleSelectChange} disabled={disabled}>
          {availableOptions.map((option) => (
            <Option key={`${selectedServiceTeam}_${field.name}_${option}_opt`} value={option}>
              {option}
            </Option>
          ))}
        </Select>
      )}
    </Stack>
  );
}
